import "pixi.js";

/**
 * BaseView - abstraction for a display object container with utility methods.
 * Automatically destroyed when removed from the scene.
 */
class BaseView extends PIXI.Container
{

    constructor()
    {
        super();
        this.once('removed', this.destroy, this);
        this.app = PIXI.app;
        this.tweens = [];
        this.events = [];

        this._layout = this.app.layout;
        this._sound = this.app.sound;
        this._config = this.app.config;
        this._nav = this.app.nav;
        this._assets = this.app.assets;
        this._model = this.app.model;
        this._dispatcher = this.app.dispatcher;
        this._controller = this.app.controller;
        this._comm = this.app.comm;
        this._locale = this.app.locale;
        this._localData = this.app.localData;
    }

    // View injection of potentially needed dependencies. Also used to improve auto-complete

    /** @returns {Layout|*} */ get layout() { return this._layout; }
    /** @returns {SoundController|*} */ get sound() { return this._sound; }
    /** @returns {Config|*} */ get config() { return this._config; }
    /** @returns {ViewManager|*} */ get nav() { return this._nav; }
    /** @returns {Assets|*} */ get assets() { return this._assets; }
    /** @returns {Model|*} */ get model() { return this._model; }
    /** @returns {PIXI.utils.EventEmitter|*} */ get dispatcher() { return this._dispatcher; }
    /** @returns {GameController|*} */ get controller() { return this._controller; }
    /** @returns {Comm|*} */ get comm() { return this._comm; }
    /** @returns {Locale|*} */ get locale() { return this._locale; }
    /** @returns {LocalData|*} */ get localData() { return this._localData; }


    /**
     * Add a listener which is automatically disposed when the view is disposed
     * @param emitter
     * @param eventType
     * @param callback
     * @param scope
     */
    addListener(emitter, eventType, callback, scope)
    {
        this.events.push({emitter:emitter, eventType:eventType, callback:callback, scope:scope});
        emitter.on(eventType, callback, scope);
    }


    /**
     * Not fully implemented.
     * The idea is to keep track of all delayed calls so they can be killed when the view is destroyed.
     * @param delay
     * @param callback
     * @param args
     * @param scope
     */
    addDelayedCall(delay, callback, args, scope)
    {
        TweenMax.delayedCall(delay, callback, args, scope);
    }


    /**
     * Convenience function.
     */
    removeFromParent()
    {
        if (this.parent)
        {
            this.parent.removeChild(this);
        }
    }


    delayedRemoveChild(child, delay)
    {
        this.addDelayedCall(delay, this.removeChild, [child], this);
    }


    destroyObj(child)
    {
        TweenMax.killTweensOf(child);
        if (child.parent)
        {
            child.parent.removeChild(child);
        }
        child = null;
    }


    /**
     * Dispose of the object, called automatically when removed from stage
     */
    destroy()
    {
        const numTweens = this.tweens.length;
        for (let tweenIdx = 0; tweenIdx < numTweens; tweenIdx++)
        {
            let t = this.tweens[tweenIdx];
            t.kill();
        }

        for (let evtIdx = 0; evtIdx < this.events.length; evtIdx++)
        {
            let evtData = this.events[evtIdx];
            let emitter = evtData.emitter;
            emitter.removeListener(evtData.eventType, evtData.callback, evtData.scope);
        }

        this.removeChildren();
        this.removeAllListeners();
    }


}

export default BaseView;